import type { AnimationI, AutoAnimatePresetId, ScheduledAnimation } from "tweenly"
import {
    getRandomPair,
    PRESET_PAIRS,
    roundToFramerate,
    scheduleInitAnimations,
    scheduleIntroAnimations,
    scheduleOutroAnimations,
    TOP_LEVEL_STAGGER,
} from "tweenly"
import { v4 as uuidv4 } from "uuid"
import { selectUndoable } from "../helpers/selector.helpers"
import {
    addSelectedAnimationsAction,
    clearSelectedAnimationsAction,
} from "../store/slices/activeAnimation.slice"
import { updateMasterTimeline } from "../store/slices/masterTimeline.slice"
import {
    addAutoAnimationsAction,
    deleteAnimationsAction,
    selectObjects,
    selectRootObjects,
} from "../store/slices/objects.slice"
import { selectAllAnimations } from "../store/slices/objects.slice/animation/selectAllAnimations"
import useFramerate from "./useFramerate"
import { useAppDispatch, useAppSelector, useAppStore } from "./useRedux"

// Re-export types and presets for consumers
export { AUTO_ANIMATE_PRESETS } from "../helpers/autoAnimate.helpers"
export type { AutoAnimatePreset } from "../helpers/autoAnimate.helpers"
export type { AutoAnimatePresetId } from "tweenly"

export const useAutoAnimations = () => {
    const dispatch = useAppDispatch()
    const objectsState = useAppSelector((state) => selectUndoable(state).objects)
    // read on demand, never subscribed — see useTimelineActions
    const store = useAppStore()
    const getTimeToSeek = () => store.getState().masterTimeline.time
    const { framerate } = useFramerate()

    const hasAnyAnimations = (): boolean => {
        return selectObjects(objectsState).some(
            (obj) => obj && obj.animations && Object.keys(obj.animations).length > 0
        )
    }

    const generateIntroOutro = (presetId: AutoAnimatePresetId = "fade") => {
        const rootObjects = selectRootObjects(objectsState)
        if (rootObjects.length === 0) return

        // Phase 1: Schedule intro animations
        const introSchedule: ScheduledAnimation[] = []
        let maxIntroEnd = 0

        rootObjects.forEach((obj, i) => {
            const pair = presetId === "random" ? getRandomPair() : PRESET_PAIRS[presetId]
            const startDelay = roundToFramerate(i * TOP_LEVEL_STAGGER, framerate)
            const endTime = scheduleIntroAnimations(obj.id, startDelay, true, 0, {
                objectsById: objectsState.value,
                pair,
                framerate,
                scheduled: introSchedule,
            })
            maxIntroEnd = Math.max(maxIntroEnd, endTime)
        })

        // Phase 2: Schedule outro animations
        const rootObjectIds = rootObjects.map((obj) => obj.id)
        const outroSchedule = scheduleOutroAnimations(
            introSchedule,
            rootObjectIds,
            maxIntroEnd,
            presetId,
            framerate
        )

        // Phase 3: Schedule init animations
        const initSchedule = scheduleInitAnimations(introSchedule, framerate)

        // Phase 4: Dispatch all animations in a single batch action
        const allScheduledAnimations = [...initSchedule, ...introSchedule, ...outroSchedule]
        const animationsToAdd = allScheduledAnimations.map((anim) => ({
            animation: {
                id: uuidv4(),
                target: null,
                title: anim.title,
                objectId: anim.objectId,
                duration: anim.duration,
                delay: anim.delay,
                repeat: 0,
                tween: anim.tween,
                relativeTo: null,
                ease: anim.ease,
                onInitCode: null,
                onUpdateCode: null,
                subTimeline: false,
            },
            objectId: anim.objectId,
        }))

        dispatch(addAutoAnimationsAction({ animations: animationsToAdd }))

        dispatch(updateMasterTimeline(getTimeToSeek()))
    }

    const clearAllAnimations = () => {
        const allAnimations: AnimationI[] = []
        selectObjects(objectsState).forEach((obj) => {
            if (obj?.animations) {
                Object.values(obj.animations).forEach((anim) => {
                    if (anim) allAnimations.push(anim)
                })
            }
        })
        if (allAnimations.length > 0) {
            dispatch(deleteAnimationsAction({ animations: allAnimations }))
            dispatch(updateMasterTimeline(0))
        }
    }

    const selectAllAnimationsAction = () => {
        const allAnims = selectAllAnimations(selectObjects(objectsState))
        const ids = allAnims.map((a) => a.id)
        if (ids.length > 0) {
            dispatch(clearSelectedAnimationsAction())
            dispatch(addSelectedAnimationsAction(ids))
        }
    }

    const deselectAllAnimations = () => {
        dispatch(clearSelectedAnimationsAction())
    }

    return {
        generateIntroOutro,
        clearAllAnimations,
        selectAllAnimationsAction,
        deselectAllAnimations,
        hasAnyAnimations,
        objectCount: selectRootObjects(objectsState).length,
    }
}
